import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function hourLabel(h) {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
}

export default function DigestScheduleDialog({ open, onOpenChange, chatId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sch, setSch] = useState({ enabled: false, day: 0, hour: 9 });

  useEffect(() => {
    if (!open || !chatId) return;
    setLoading(true);
    api.get(`/meetings/projects/${chatId}/digest-schedule`)
      .then(({ data }) => setSch({ enabled: !!data.enabled, day: data.day ?? 0, hour: data.hour ?? 9 }))
      .catch(() => setSch({ enabled: false, day: 0, hour: 9 }))
      .finally(() => setLoading(false));
  }, [open, chatId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/meetings/projects/${chatId}/digest-schedule`, sch);
      toast.success(sch.enabled ? `Weekly recap set for ${DAYS[sch.day]} at ${hourLabel(sch.hour)}` : "Weekly recap turned off");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not save schedule");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px]" data-testid="digest-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-brand" /> Weekly recap schedule</DialogTitle>
          <DialogDescription>Automatically post &amp; email this project's recap on a set day and time.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-ink-mute" /></div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-[14px] text-ink">Send a weekly recap automatically</span>
              <input
                type="checkbox"
                data-testid="digest-schedule-enabled"
                checked={sch.enabled}
                onChange={(e) => setSch({ ...sch, enabled: e.target.checked })}
                className="w-5 h-5 accent-[var(--brand,#22c55e)]"
              />
            </label>

            <div className={sch.enabled ? "" : "opacity-40 pointer-events-none"}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wide text-ink-mute block mb-1">Day</label>
                  <select
                    data-testid="digest-schedule-day"
                    value={sch.day}
                    onChange={(e) => setSch({ ...sch, day: Number(e.target.value) })}
                    className="w-full h-9 rounded-lg bg-bg border border-hairline px-2 text-sm text-ink"
                  >
                    {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wide text-ink-mute block mb-1">Time</label>
                  <select
                    data-testid="digest-schedule-hour"
                    value={sch.hour}
                    onChange={(e) => setSch({ ...sch, hour: Number(e.target.value) })}
                    className="w-full h-9 rounded-lg bg-bg border border-hairline px-2 text-sm text-ink"
                  >
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-ink-mute mt-2">Times are in UTC.</p>
            </div>

            <button
              onClick={save}
              disabled={saving}
              data-testid="digest-schedule-save"
              className="w-full h-10 rounded-xl bg-brand text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save schedule
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
